import { FormGroup } from '@angular/forms';
import {
  CutOffTimesAndFixingSources,
  KeyValue,
  MarketDataSpreadingRule,
  RfqLegSettings
} from '@approot/models';
import { GetImpliedVolPayload, StrikePayload } from '@approot/services';
import { DateUtilities, Utilities } from '@approot/utilities';
import { getStrikeValue } from '../services/leg-spot-strike.service';

export function volatilityPayload(
  volNumber: string,
  form: FormGroup,
  presetsLoaded: boolean,
  legSyncForwardPoints: boolean,
  getMarketDataTimeStamp: string,
  cutOffTimesAndFixingSources: CutOffTimesAndFixingSources,
  marketDataSpreadingRule: MarketDataSpreadingRule,
  rfqExecutionSettings: RfqLegSettings
): GetImpliedVolPayload {
  const canGet = canGetVols(
    volNumber,
    form,
    legSyncForwardPoints,
    presetsLoaded,
    rfqExecutionSettings
  );
  if (!canGet) {
    return null;
  }

  const isFixingOrCutOff = form.get('cutOff') ? 'cutOffs' : 'fixingSources';
  const cutOffOrFixingValue = form.get(
    isFixingOrCutOff === 'cutOffs' ? 'cutOff' : 'fixingSource'
  ).value;

  const payload: GetImpliedVolPayload = {
    task: 'getImpliedVol',
    expirationDate: DateUtilities.prettyDateToDate(
      form.get('expiryDate').value
    ),
    timestamp: getMarketDataTimeStamp,
    currencyPair: form.get('cross').value,
    datesAndStrikes: [
      {
        date: DateUtilities.prettyDateToDate(form.get('expiryDate').value),
        strike: getStrikeValue(form.get('strike' + volNumber))
      }
    ],
    cutOffOrFixingCode: cutOffTimesAndFixingSources[isFixingOrCutOff].find(
      y => y.longName === cutOffOrFixingValue
    ).code
  };

  if (marketDataSpreadingRule) {
    payload.marketDataSpreadingRule = marketDataSpreadingRule;
  }

  return payload;
}

export function strikePayload(
  form: FormGroup,
  strikeNumber: string,
  formattedProduct: KeyValue,
  valueToUse: string,
  timestamp: string
): StrikePayload {
  const strikeValue = valueToUse;
  const expiryDateCtrl = form.get('expiryDate');
  const allowStrikeCalc =
    form.get('counterNotional' + strikeNumber) &&
    form.get('cross')?.valid &&
    form.get('notional' + strikeNumber) &&
    isNaN(+strikeValue) &&
    (expiryDateCtrl?.valid || strikeValue === 's');

  if (!allowStrikeCalc) {
    return null;
  }
  let payload: StrikePayload = {
    task: {
      ...formattedProduct,
      strike: strikeValue
    },
    timestamp
  };

  if (Utilities.isValidDelta(strikeValue)) {
    const optionTypeCtrl = form.get('optionType' + strikeNumber);
    if (optionTypeCtrl?.valid) {
      payload.task.optionType = optionTypeCtrl.value;
    } else {
      return null;
    }
  }

  return payload;
}

function canGetVols(
  volNumber: string,
  form: FormGroup,
  legSyncForwardPoints: boolean,
  presetsLoaded: boolean,
  rfqExecutionSettings: RfqLegSettings
): boolean {
  const strikeCtrl = form.get('strike' + volNumber);
  const volBidCtrl = form.get('vol' + volNumber + 'Bid');
  const volOfferCtrl = form.get('vol' + volNumber + 'Ask');
  return (
    strikeCtrl?.valid &&
    !isNaN(getStrikeValue(strikeCtrl)) && // numbers only
    form.get('expiryDate')?.valid &&
    form.get('cross')?.valid &&
    volBidCtrl &&
    volOfferCtrl &&
    (form.get('cutOff')?.value || form.get('fixingSource')?.value) &&
    (!legSyncForwardPoints ||
      presetsLoaded ||
      (!volBidCtrl.value &&
        !volOfferCtrl.value &&
        rfqExecutionSettings.providerLeg))
  );
}
